import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Plus, Upload, Check, X, Trash2, Shirt, Home, Package, LogOut, Wallet, Receipt, FileText } from 'lucide-react';

const fC = (v) => {
  if (!v || v === 0) return '₹0';
  const n = Number(v);
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(0)}K`;
  return `₹${n.toLocaleString('en-IN')}`;
};

export default function KitExpensesTab({ personId, canEdit }) {
  const [activeSubTab, setActiveSubTab] = useState('uniform');
  const [config, setConfig] = useState({ uniform_types: [], asset_items: [], expense_categories: [], claim_types: [], conditions: [] });

  useEffect(() => {
    api.get('/people-kit/config').then(r => setConfig(r.data)).catch(() => {});
  }, []);

  const subTabs = [
    { id: 'uniform', label: 'Uniform', icon: Shirt },
    { id: 'accommodation', label: 'Accommodation', icon: Home },
    { id: 'assets', label: 'Assets Issued', icon: Package },
    { id: 'training', label: 'SOP Training', icon: FileText },
    { id: 'petty', label: 'Petty Cash', icon: Wallet },
    { id: 'claims', label: 'Expense Claims', icon: Receipt },
    { id: 'exit', label: 'Exit Clearance', icon: LogOut },
  ];

  return (
    <div className="space-y-4" data-testid="tab-kit-expenses">
      {/* Sub-tab navigation */}
      <div className="flex gap-1 overflow-x-auto border-b" style={{ borderColor: '#E5E1DC' }}>
        {subTabs.map(t => {
          const Icon = t.icon;
          const active = activeSubTab === t.id;
          return (
            <button key={t.id} onClick={() => setActiveSubTab(t.id)} data-testid={`subtab-${t.id}`}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium whitespace-nowrap border-b-2 ${active ? '' : 'border-transparent'}`}
              style={active ? { color: '#1B2A4A', borderColor: '#1B2A4A' } : { color: '#6B7280' }}>
              <Icon size={12} /> {t.label}
            </button>
          );
        })}
      </div>

      {activeSubTab === 'uniform' && <UniformPanel personId={personId} canEdit={canEdit} types={config.uniform_types} conditions={config.conditions} />}
      {activeSubTab === 'accommodation' && <AccommodationPanel personId={personId} canEdit={canEdit} />}
      {activeSubTab === 'assets' && <AssetsIssuedPanel personId={personId} canEdit={canEdit} items={config.asset_items} conditions={config.conditions} />}
      {activeSubTab === 'training' && <SopTrainingPanel personId={personId} />}
      {activeSubTab === 'petty' && <PettyCashPanel personId={personId} canEdit={canEdit} />}
      {activeSubTab === 'claims' && <ClaimsPanel personId={personId} canEdit={canEdit} categories={config.expense_categories} claimTypes={config.claim_types} />}
      {activeSubTab === 'exit' && <ExitClearancePanel personId={personId} />}
    </div>
  );
}

// ============================================================
// 1) UNIFORM
// ============================================================
function UniformPanel({ personId, canEdit, types, conditions }) {
  const [data, setData] = useState({});
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  const refresh = () => api.get(`/people-kit/${personId}/uniform`).then(r => setData(r.data)).catch(() => setData({}));
  useEffect(() => { refresh(); }, [personId]);

  const save = async () => {
    await api.put(`/people-kit/${personId}/uniform`, form);
    setEditing(false); refresh();
  };

  if (!editing) {
    return (
      <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E5E1DC' }}>
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Uniform Details</p>
          {canEdit && <button onClick={() => { setEditing(true); setForm(data); }} className="text-xs px-2 py-1 rounded hover:bg-gray-50" style={{ color: '#2A7B88' }} data-testid="edit-uniform-btn">Edit</button>}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          <Fact l="Type" v={data.uniform_type} />
          <Fact l="Shirt Size" v={data.shirt_size} />
          <Fact l="Trouser Size" v={data.trouser_size} />
          <Fact l="Coat Size" v={data.coat_size} />
          <Fact l="Shoe Size" v={data.shoe_size} />
          <Fact l="Sets Issued" v={data.sets_issued} />
          <Fact l="Condition" v={data.condition} />
          <Fact l="Last Issued" v={data.last_issued_date} />
          <Fact l="Next Issue Due" v={data.next_issue_due} />
          {data.notes && <div className="col-span-2 sm:col-span-4"><Fact l="Notes" v={data.notes} /></div>}
        </div>
        {!data.uniform_type && <p className="text-xs mt-3" style={{ color: '#9CA3AF' }}>No uniform details recorded yet.</p>}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border p-4 space-y-3" style={{ borderColor: '#2A7B88', borderWidth: 2 }}>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Uniform Type</label>
          <select value={form.uniform_type || ''} onChange={e => setForm(f => ({ ...f, uniform_type: e.target.value }))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }}>
            <option value="">— Select —</option>
            {(types || []).map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
        <Field l="Shirt Size" v={form.shirt_size} o={v => setForm(f => ({ ...f, shirt_size: v }))} />
        <Field l="Trouser Size" v={form.trouser_size} o={v => setForm(f => ({ ...f, trouser_size: v }))} />
        <Field l="Coat Size" v={form.coat_size} o={v => setForm(f => ({ ...f, coat_size: v }))} />
        <Field l="Shoe Size" v={form.shoe_size} o={v => setForm(f => ({ ...f, shoe_size: v }))} />
        <Field l="Sets Issued" v={form.sets_issued} o={v => setForm(f => ({ ...f, sets_issued: v }))} t="number" />
        <Field l="Last Issued Date" v={form.last_issued_date} o={v => setForm(f => ({ ...f, last_issued_date: v }))} t="date" />
        <Field l="Next Issue Due" v={form.next_issue_due} o={v => setForm(f => ({ ...f, next_issue_due: v }))} t="date" />
        <div>
          <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Condition</label>
          <select value={form.condition || ''} onChange={e => setForm(f => ({ ...f, condition: e.target.value }))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }}>
            <option value="">—</option>
            {(conditions || []).map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div className="sm:col-span-3"><Field l="Notes" v={form.notes} o={v => setForm(f => ({ ...f, notes: v }))} /></div>
      </div>
      <div className="flex gap-2">
        <button onClick={save} className="px-4 py-2 rounded-md text-sm font-medium text-white" style={{ backgroundColor: '#1B2A4A' }} data-testid="save-uniform-btn">Save</button>
        <button onClick={() => setEditing(false)} className="px-4 py-2 rounded-md text-sm border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
      </div>
    </div>
  );
}

// ============================================================
// 2) ACCOMMODATION
// ============================================================
function AccommodationPanel({ personId, canEdit }) {
  const [data, setData] = useState({});
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  const refresh = () => api.get(`/people-kit/${personId}/accommodation`).then(r => setData(r.data)).catch(() => setData({ provided: false }));
  useEffect(() => { refresh(); }, [personId]);

  const save = async () => {
    await api.put(`/people-kit/${personId}/accommodation`, form);
    setEditing(false); refresh();
  };

  if (!editing) {
    return (
      <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E5E1DC' }}>
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Staff Accommodation</p>
          {canEdit && <button onClick={() => { setEditing(true); setForm(data); }} className="text-xs px-2 py-1 rounded hover:bg-gray-50" style={{ color: '#2A7B88' }} data-testid="edit-accommodation-btn">Edit</button>}
        </div>
        {!data.provided ? (
          <p className="text-xs" style={{ color: '#9CA3AF' }}>Accommodation not provided.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <Fact l="Location" v={data.location} />
            <Fact l="Room #" v={data.room_number} />
            <Fact l="Check-in" v={data.check_in_date} />
            <Fact l="Bedding" v={data.bedding_issued ? `Issued (${data.bedding_condition || 'Good'})` : 'Not issued'} />
            <Fact l="Room Key" v={data.room_key_issued ? 'Issued' : 'Not issued'} />
            {data.notes && <div className="col-span-2 sm:col-span-4"><Fact l="Notes" v={data.notes} /></div>}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border p-4 space-y-3" style={{ borderColor: '#2A7B88', borderWidth: 2 }}>
      <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!form.provided} onChange={e => setForm(f => ({ ...f, provided: e.target.checked }))} /> Accommodation provided by company</label>
      {form.provided && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field l="Location" v={form.location} o={v => setForm(f => ({ ...f, location: v }))} ph="Staff Quarters / Hotel" />
          <Field l="Room Number" v={form.room_number} o={v => setForm(f => ({ ...f, room_number: v }))} />
          <Field l="Check-in Date" v={form.check_in_date} o={v => setForm(f => ({ ...f, check_in_date: v }))} t="date" />
          <label className="flex items-center gap-2 text-xs mt-6"><input type="checkbox" checked={!!form.bedding_issued} onChange={e => setForm(f => ({ ...f, bedding_issued: e.target.checked }))} /> Bedding issued</label>
          <Field l="Bedding Condition" v={form.bedding_condition} o={v => setForm(f => ({ ...f, bedding_condition: v }))} />
          <label className="flex items-center gap-2 text-xs mt-6"><input type="checkbox" checked={!!form.room_key_issued} onChange={e => setForm(f => ({ ...f, room_key_issued: e.target.checked }))} /> Room key issued</label>
          <div className="sm:col-span-3"><Field l="Notes" v={form.notes} o={v => setForm(f => ({ ...f, notes: v }))} /></div>
        </div>
      )}
      <div className="flex gap-2">
        <button onClick={save} className="px-4 py-2 rounded-md text-sm font-medium text-white" style={{ backgroundColor: '#1B2A4A' }} data-testid="save-accommodation-btn">Save</button>
        <button onClick={() => setEditing(false)} className="px-4 py-2 rounded-md text-sm border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
      </div>
    </div>
  );
}

// ============================================================
// 3) ASSETS ISSUED (list with add/remove)
// ============================================================
function AssetsIssuedPanel({ personId, canEdit, items, conditions }) {
  const [data, setData] = useState({ items: [], total: 0, outstanding: 0 });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ condition_at_issue: 'Good', issue_date: new Date().toISOString().slice(0, 10) });

  const refresh = () => api.get(`/people-kit/${personId}/assets-issued`).then(r => setData(r.data)).catch(() => setData({ items: [], total: 0, outstanding: 0 }));
  useEffect(() => { refresh(); }, [personId]);

  const save = async () => {
    if (!form.item_name) { alert('Item name required'); return; }
    await api.post(`/people-kit/${personId}/assets-issued`, form);
    setShowForm(false); setForm({ condition_at_issue: 'Good', issue_date: new Date().toISOString().slice(0, 10) });
    refresh();
  };

  const returnAsset = async (asset) => {
    const returnDate = prompt('Return date (YYYY-MM-DD):', new Date().toISOString().slice(0, 10));
    if (!returnDate) return;
    const returnCondition = prompt('Return condition (Excellent/Good/Fair/Poor/Damaged):', 'Good');
    if (!returnCondition) return;
    await api.put(`/people-kit/${personId}/assets-issued/${asset.id}`, { return_date: returnDate, return_condition: returnCondition });
    refresh();
  };

  const del = async (id) => {
    if (!window.confirm('Delete this record?')) return;
    await api.delete(`/people-kit/${personId}/assets-issued/${id}`);
    refresh();
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Company Assets Issued</p>
          <p className="text-[11px]" style={{ color: '#9CA3AF' }}>{data.total} total · <span style={{ color: data.outstanding > 0 ? '#D97706' : '#2D8B5E' }}>{data.outstanding} outstanding</span></p>
        </div>
        {canEdit && <button onClick={() => setShowForm(true)} className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium text-white" style={{ backgroundColor: '#2A7B88' }} data-testid="add-asset-btn"><Plus size={12} /> Issue Asset</button>}
      </div>

      {showForm && (
        <div className="bg-white rounded-lg border p-4 space-y-3" style={{ borderColor: '#2A7B88', borderWidth: 2 }}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Item</label>
              <select value={form.item_name || ''} onChange={e => setForm(f => ({ ...f, item_name: e.target.value }))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }}>
                <option value="">— Select —</option>
                {(items || []).map(i => <option key={i}>{i}</option>)}
              </select>
            </div>
            <Field l="Serial Number" v={form.serial_number} o={v => setForm(f => ({ ...f, serial_number: v }))} />
            <Field l="Issue Date" v={form.issue_date} o={v => setForm(f => ({ ...f, issue_date: v }))} t="date" />
            <div>
              <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Condition at Issue</label>
              <select value={form.condition_at_issue || 'Good'} onChange={e => setForm(f => ({ ...f, condition_at_issue: e.target.value }))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }}>
                {(conditions || ['Excellent', 'Good', 'Fair']).map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2"><Field l="Notes" v={form.notes} o={v => setForm(f => ({ ...f, notes: v }))} /></div>
          </div>
          <div className="flex gap-2">
            <button onClick={save} className="px-4 py-2 rounded-md text-sm font-medium text-white" style={{ backgroundColor: '#1B2A4A' }}>Issue</button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-md text-sm border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
          </div>
        </div>
      )}

      {data.items.length === 0 ? (
        <p className="text-xs py-4" style={{ color: '#9CA3AF' }}>No assets issued.</p>
      ) : (
        <div className="bg-white rounded-lg border overflow-x-auto" style={{ borderColor: '#E5E1DC' }}>
          <table className="w-full text-xs">
            <thead style={{ backgroundColor: '#F7F5F2' }}>
              <tr><th className="text-left px-3 py-2" style={{ color: '#6B7280' }}>Item</th><th className="text-left px-3 py-2" style={{ color: '#6B7280' }}>S/N</th><th className="text-left px-3 py-2" style={{ color: '#6B7280' }}>Issued</th><th className="text-left px-3 py-2" style={{ color: '#6B7280' }}>At Issue</th><th className="text-left px-3 py-2" style={{ color: '#6B7280' }}>Returned</th><th className="text-right px-3 py-2" style={{ color: '#6B7280' }}></th></tr>
            </thead>
            <tbody>
              {data.items.map(a => (
                <tr key={a.id} className="border-t" style={{ borderColor: '#F3F4F6' }}>
                  <td className="px-3 py-2" style={{ color: '#1B2A4A' }}>{a.item_name}</td>
                  <td className="px-3 py-2" style={{ color: '#6B7280' }}>{a.serial_number || '—'}</td>
                  <td className="px-3 py-2" style={{ color: '#6B7280' }}>{a.issue_date || '—'}</td>
                  <td className="px-3 py-2" style={{ color: '#6B7280' }}>{a.condition_at_issue || '—'}</td>
                  <td className="px-3 py-2">
                    {a.return_date ? <span style={{ color: '#2D8B5E' }}>{a.return_date} · {a.return_condition}</span> : <span style={{ color: '#D97706' }}>Outstanding</span>}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {!a.return_date && canEdit && <button onClick={() => returnAsset(a)} className="text-[10px] px-2 py-0.5 rounded bg-green-50 mr-1" style={{ color: '#2D8B5E' }}>Mark Return</button>}
                    {canEdit && <button onClick={() => del(a.id)} className="text-[10px] px-2 py-0.5 rounded hover:bg-red-50" style={{ color: '#D4726A' }}><Trash2 size={10} /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================
// 4) PETTY CASH
// ============================================================
function PettyCashPanel({ personId, canEdit }) {
  const [data, setData] = useState({ entries: [], outstanding_amount: 0, open_count: 0, overdue_count: 0 });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ advance_date: new Date().toISOString().slice(0, 10) });
  const [settleId, setSettleId] = useState(null);
  const [settleSpent, setSettleSpent] = useState('');

  const refresh = () => api.get(`/people-kit/${personId}/petty-cash`).then(r => setData(r.data)).catch(() => {});
  useEffect(() => { refresh(); }, [personId]);

  const save = async () => {
    if (!form.purpose || !form.amount) { alert('Purpose and amount required'); return; }
    await api.post(`/people-kit/${personId}/petty-cash`, form);
    setShowForm(false); setForm({ advance_date: new Date().toISOString().slice(0, 10) });
    refresh();
  };

  const settle = async (id) => {
    const spent = parseFloat(settleSpent);
    if (isNaN(spent)) { alert('Enter valid amount spent'); return; }
    await api.put(`/people-kit/${personId}/petty-cash/${id}`, { amount_spent: spent });
    setSettleId(null); setSettleSpent('');
    refresh();
  };

  const del = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    await api.delete(`/people-kit/${personId}/petty-cash/${id}`);
    refresh();
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Petty Cash</p>
          <p className="text-[11px]">
            Outstanding: <span className="font-medium" style={{ color: data.outstanding_amount > 0 ? '#D97706' : '#2D8B5E' }}>{fC(data.outstanding_amount)}</span>
            {data.overdue_count > 0 && <span className="ml-2" style={{ color: '#DC2626' }}>· {data.overdue_count} OVERDUE</span>}
          </p>
        </div>
        {canEdit && <button onClick={() => setShowForm(true)} className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium text-white" style={{ backgroundColor: '#2A7B88' }} data-testid="add-petty-btn"><Plus size={12} /> Give Advance</button>}
      </div>

      {showForm && (
        <div className="bg-white rounded-lg border p-4 space-y-3" style={{ borderColor: '#2A7B88', borderWidth: 2 }}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field l="Date" v={form.advance_date} o={v => setForm(f => ({ ...f, advance_date: v }))} t="date" />
            <Field l="Amount ₹" v={form.amount} o={v => setForm(f => ({ ...f, amount: v }))} t="number" />
            <Field l="Settle By" v={form.settlement_deadline} o={v => setForm(f => ({ ...f, settlement_deadline: v }))} t="date" />
            <div className="sm:col-span-3"><Field l="Purpose" v={form.purpose} o={v => setForm(f => ({ ...f, purpose: v }))} ph="e.g. Market purchases for Teal Door Cafe" /></div>
            <Field l="Approved By" v={form.approved_by} o={v => setForm(f => ({ ...f, approved_by: v }))} />
          </div>
          <div className="flex gap-2">
            <button onClick={save} className="px-4 py-2 rounded-md text-sm font-medium text-white" style={{ backgroundColor: '#1B2A4A' }} data-testid="save-petty-btn">Give Advance</button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-md text-sm border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
          </div>
        </div>
      )}

      {data.entries.length === 0 ? (
        <p className="text-xs py-4" style={{ color: '#9CA3AF' }}>No petty cash entries.</p>
      ) : (
        <div className="bg-white rounded-lg border overflow-x-auto" style={{ borderColor: '#E5E1DC' }}>
          <table className="w-full text-xs">
            <thead style={{ backgroundColor: '#F7F5F2' }}>
              <tr><th className="text-left px-3 py-2" style={{ color: '#6B7280' }}>Date</th><th className="text-left px-3 py-2" style={{ color: '#6B7280' }}>Purpose</th><th className="text-right px-3 py-2" style={{ color: '#6B7280' }}>Amount</th><th className="text-right px-3 py-2" style={{ color: '#6B7280' }}>Spent</th><th className="text-right px-3 py-2" style={{ color: '#6B7280' }}>Balance</th><th className="text-left px-3 py-2" style={{ color: '#6B7280' }}>Status</th><th className="text-right px-3 py-2"></th></tr>
            </thead>
            <tbody>
              {data.entries.map(e => (
                <tr key={e.id} className="border-t" style={{ borderColor: '#F3F4F6' }}>
                  <td className="px-3 py-2" style={{ color: '#6B7280' }}>{e.advance_date}</td>
                  <td className="px-3 py-2" style={{ color: '#1B2A4A' }}>{e.purpose}</td>
                  <td className="px-3 py-2 text-right" style={{ color: '#1B2A4A' }}>{fC(e.amount)}</td>
                  <td className="px-3 py-2 text-right" style={{ color: '#6B7280' }}>{fC(e.amount_spent)}</td>
                  <td className="px-3 py-2 text-right font-medium" style={{ color: e.balance_outstanding > 0 ? '#D97706' : '#2D8B5E' }}>{fC(e.balance_outstanding)}</td>
                  <td className="px-3 py-2">
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={{
                      backgroundColor: e.status === 'Settled' ? '#ECFDF5' : e.status === 'Overdue' ? '#FEF2F2' : '#FEF3C7',
                      color: e.status === 'Settled' ? '#2D8B5E' : e.status === 'Overdue' ? '#DC2626' : '#D97706',
                    }}>{e.status}</span>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {e.status !== 'Settled' && canEdit && (
                      settleId === e.id ? (
                        <span className="inline-flex items-center gap-1">
                          <input type="number" value={settleSpent} onChange={ev => setSettleSpent(ev.target.value)} placeholder="Spent ₹" className="w-20 px-2 py-1 text-[10px] rounded border" style={{ borderColor: '#E5E1DC' }} />
                          <button onClick={() => settle(e.id)} className="text-[10px] px-1.5 py-0.5 rounded bg-green-50" style={{ color: '#2D8B5E' }}><Check size={10} /></button>
                          <button onClick={() => setSettleId(null)} className="text-[10px] px-1.5 py-0.5 rounded"><X size={10} /></button>
                        </span>
                      ) : (
                        <button onClick={() => { setSettleId(e.id); setSettleSpent(String(e.amount)); }} className="text-[10px] px-2 py-0.5 rounded bg-blue-50 mr-1" style={{ color: '#185FA5' }} data-testid={`settle-petty-${e.id}`}>Settle</button>
                      )
                    )}
                    {canEdit && <button onClick={() => del(e.id)} className="text-[10px] px-2 py-0.5 rounded hover:bg-red-50" style={{ color: '#D4726A' }}><Trash2 size={10} /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================
// 5) EXPENSE CLAIMS
// ============================================================
function ClaimsPanel({ personId, canEdit, categories, claimTypes }) {
  const [data, setData] = useState({ claims: [], total: 0, pending: 0, approved: 0, paid: 0, total_pending_amount: 0 });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ claim_type: 'Reimbursement', category: 'Other', claim_date: new Date().toISOString().slice(0, 10) });
  const [uploading, setUploading] = useState(false);

  const refresh = () => api.get(`/people-kit/${personId}/expense-claims`).then(r => setData(r.data)).catch(() => {});
  useEffect(() => { refresh(); }, [personId]);

  const uploadBill = async (ev) => {
    const file = ev.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file); fd.append('folder', 'expense-bills');
      const r = await api.post('/cloudinary/upload', fd);
      setForm(f => ({ ...f, bill_url: r.data.url }));
    } catch { alert('Bill upload failed'); }
    setUploading(false);
  };

  const save = async () => {
    if (!form.description || !form.amount) { alert('Description and amount required'); return; }
    await api.post(`/people-kit/${personId}/expense-claims`, form);
    setShowForm(false); setForm({ claim_type: 'Reimbursement', category: 'Other', claim_date: new Date().toISOString().slice(0, 10) });
    refresh();
  };

  const action = async (claimId, kind) => {
    if (kind === 'reject') {
      const reason = prompt('Reason for rejection:');
      if (!reason) return;
      await api.post(`/people-kit/${personId}/expense-claims/${claimId}/reject`, { reason });
    } else {
      await api.post(`/people-kit/${personId}/expense-claims/${claimId}/${kind}`);
    }
    refresh();
  };

  const del = async (id) => {
    if (!window.confirm('Delete this claim?')) return;
    await api.delete(`/people-kit/${personId}/expense-claims/${id}`);
    refresh();
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Expense Claims</p>
          <p className="text-[11px]" style={{ color: '#9CA3AF' }}>
            {data.pending} pending ({fC(data.total_pending_amount)}) · {data.approved} approved · {data.paid} paid · {data.rejected || 0} rejected
          </p>
        </div>
        {canEdit && <button onClick={() => setShowForm(true)} className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium text-white" style={{ backgroundColor: '#2A7B88' }} data-testid="add-claim-btn"><Plus size={12} /> New Claim</button>}
      </div>

      {showForm && (
        <div className="bg-white rounded-lg border p-4 space-y-3" style={{ borderColor: '#2A7B88', borderWidth: 2 }}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Claim Type</label>
              <select value={form.claim_type} onChange={e => setForm(f => ({ ...f, claim_type: e.target.value }))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }}>
                {(claimTypes || ['Travel', 'Reimbursement']).map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Category</label>
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }}>
                {(categories || ['Other']).map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <Field l="Date" v={form.claim_date} o={v => setForm(f => ({ ...f, claim_date: v }))} t="date" />
            <Field l="Amount ₹" v={form.amount} o={v => setForm(f => ({ ...f, amount: v }))} t="number" />
            <Field l="Outlet (for P&L)" v={form.outlet} o={v => setForm(f => ({ ...f, outlet: v }))} ph="e.g. Teal Door Cafe" />
            <div className="sm:col-span-3"><Field l="Description" v={form.description} o={v => setForm(f => ({ ...f, description: v }))} /></div>
            {form.claim_type === 'Travel' && (
              <>
                <Field l="From" v={form.from_location} o={v => setForm(f => ({ ...f, from_location: v }))} />
                <Field l="To" v={form.to_location} o={v => setForm(f => ({ ...f, to_location: v }))} />
                <Field l="Mode" v={form.mode} o={v => setForm(f => ({ ...f, mode: v }))} ph="Car / Train / Flight" />
                <Field l="Distance (km)" v={form.distance_km} o={v => setForm(f => ({ ...f, distance_km: v }))} t="number" />
                <div className="sm:col-span-2"><Field l="Purpose" v={form.purpose} o={v => setForm(f => ({ ...f, purpose: v }))} /></div>
              </>
            )}
            <div className="sm:col-span-3">
              <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Bill / Receipt</label>
              {form.bill_url ? (
                <div className="flex items-center gap-2 mt-1">
                  <a href={form.bill_url} target="_blank" rel="noreferrer" className="text-[10px] px-2 py-1 rounded bg-blue-50" style={{ color: '#185FA5' }}><FileText size={10} className="inline" /> View bill</a>
                  <button onClick={() => setForm(f => ({ ...f, bill_url: '' }))} className="text-[10px] px-2 py-1 rounded hover:bg-red-50" style={{ color: '#D4726A' }}>Remove</button>
                </div>
              ) : (
                <label className="flex items-center gap-2 cursor-pointer">
                  <span className="flex items-center gap-1 text-xs px-3 py-1.5 rounded border mt-1 hover:bg-gray-50" style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>
                    <Upload size={11} /> {uploading ? 'Uploading...' : 'Upload bill'}
                  </span>
                  <input type="file" className="hidden" accept="image/*,.pdf" onChange={uploadBill} />
                </label>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={save} className="px-4 py-2 rounded-md text-sm font-medium text-white" style={{ backgroundColor: '#1B2A4A' }} data-testid="save-claim-btn">Submit Claim</button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-md text-sm border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
          </div>
        </div>
      )}

      {data.claims.length === 0 ? (
        <p className="text-xs py-4" style={{ color: '#9CA3AF' }}>No expense claims yet.</p>
      ) : (
        <div className="space-y-2">
          {data.claims.map(c => (
            <div key={c.id} className="bg-white rounded-lg border p-3" style={{ borderColor: '#E5E1DC' }}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium" style={{ color: '#1B2A4A' }}>{c.description}</p>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={{
                      backgroundColor: c.status === 'Paid' ? '#ECFDF5' : c.status === 'Approved' ? '#EFF6FF' : c.status === 'Rejected' ? '#FEF2F2' : '#FEF3C7',
                      color: c.status === 'Paid' ? '#2D8B5E' : c.status === 'Approved' ? '#185FA5' : c.status === 'Rejected' ? '#DC2626' : '#D97706',
                    }}>{c.status}</span>
                  </div>
                  <p className="text-[11px]" style={{ color: '#6B7280' }}>
                    {c.claim_date} · {c.claim_type} · {c.category}{c.outlet && ` · ${c.outlet}`}
                  </p>
                  {c.claim_type === 'Travel' && c.from_location && (
                    <p className="text-[10px]" style={{ color: '#9CA3AF' }}>{c.from_location} → {c.to_location} · {c.mode} · {c.distance_km}km</p>
                  )}
                  {c.rejected_reason && <p className="text-[10px] mt-1" style={{ color: '#DC2626' }}>Rejected: {c.rejected_reason}</p>}
                  {c.approved_by && c.status !== 'Pending Approval' && <p className="text-[10px]" style={{ color: '#9CA3AF' }}>By {c.approved_by}{c.paid_date && ` · Paid ${c.paid_date}`}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-bold" style={{ color: '#1B2A4A' }}>{fC(c.amount)}</p>
                  {c.bill_url && <a href={c.bill_url} target="_blank" rel="noreferrer" className="text-[10px] px-2 py-0.5 rounded bg-blue-50" style={{ color: '#185FA5' }}><FileText size={10} /></a>}
                </div>
              </div>
              {canEdit && (
                <div className="flex gap-1 mt-2 pt-2 border-t" style={{ borderColor: '#F3F4F6' }}>
                  {c.status === 'Pending Approval' && <>
                    <button onClick={() => action(c.id, 'approve')} className="text-[10px] px-2 py-0.5 rounded bg-green-50" style={{ color: '#2D8B5E' }} data-testid={`approve-claim-${c.id}`}>Approve</button>
                    <button onClick={() => action(c.id, 'reject')} className="text-[10px] px-2 py-0.5 rounded bg-red-50" style={{ color: '#DC2626' }}>Reject</button>
                  </>}
                  {c.status === 'Approved' && <button onClick={() => action(c.id, 'mark-paid')} className="text-[10px] px-2 py-0.5 rounded bg-blue-50" style={{ color: '#185FA5' }} data-testid={`pay-claim-${c.id}`}>Mark Paid</button>}
                  <button onClick={() => del(c.id)} className="text-[10px] px-2 py-0.5 rounded hover:bg-red-50 ml-auto" style={{ color: '#D4726A' }}><Trash2 size={10} /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// 6) EXIT CLEARANCE
// ============================================================
function ExitClearancePanel({ personId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/people-kit/${personId}/exit-clearance`).then(r => setData(r.data)).catch(() => setData(null));
  }, [personId]);

  if (!data) return <p className="text-xs py-4" style={{ color: '#9CA3AF' }}>Loading...</p>;

  const byCategory = {};
  data.checklist.forEach(c => {
    if (!byCategory[c.category]) byCategory[c.category] = [];
    byCategory[c.category].push(c);
  });

  return (
    <div className="space-y-3">
      <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E5E1DC' }}>
        <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Exit Clearance Checklist</p>
        <p className="text-[11px] mt-0.5" style={{ color: '#9CA3AF' }}>
          {data.person_name} · {data.outlet} · {data.total_items} items across {data.categories.length} categories
        </p>
        {data.current_status !== 'ex' && data.current_status !== 'resigned' && (
          <p className="text-[11px] mt-2 px-3 py-2 rounded" style={{ backgroundColor: '#FEF3C7', color: '#D97706' }}>
            This checklist is auto-generated for exit preparation. Activate it when employee resigns/exits.
          </p>
        )}
      </div>

      {Object.entries(byCategory).map(([cat, items]) => (
        <div key={cat} className="bg-white rounded-lg border p-4" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-xs font-semibold mb-2" style={{ color: '#1B2A4A' }}>{cat} ({items.length})</p>
          <div className="space-y-1.5">
            {items.map((c, i) => (
              <div key={i} className="flex items-start gap-2 text-xs py-1">
                <div className="w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 mt-0.5" style={{ borderColor: '#E5E1DC' }}>
                  {c.status === 'Done' && <Check size={10} style={{ color: '#2D8B5E' }} />}
                </div>
                <span style={{ color: '#1B2A4A' }}>{c.item}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// Shared helpers
function Field({ l, v, o, t = 'text', ph = '' }) {
  return (
    <div>
      <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>{l}</label>
      <input type={t} value={v || ''} onChange={e => o(e.target.value)} placeholder={ph} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
    </div>
  );
}

function Fact({ l, v }) {
  if (v === undefined || v === null || v === '') return null;
  return <div><span className="font-medium" style={{ color: '#6B7280' }}>{l}: </span><span style={{ color: '#1B2A4A' }}>{v}</span></div>;
}

// ============================================================
// 7) SOP TRAINING PANEL (New Joiner Checklist + My SOPs)
// ============================================================
function SopTrainingPanel({ personId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = () => {
    setLoading(true);
    api.get(`/outlet-intel/training/new-joiner-checklist/${personId}`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  };
  useEffect(() => { fetchData(); }, [personId]);

  const acknowledge = async (sop) => {
    try {
      await api.post(`/outlet-intel/${encodeURIComponent(sop.outlet_name)}/sops/${sop.sop_id}/acknowledge`);
      fetchData();
    } catch (e) {
      alert('Failed: ' + (e?.response?.data?.detail || 'Contact admin to link your user to a person record'));
    }
  };

  if (loading) return <p className="text-xs py-4" style={{ color: '#9CA3AF' }}>Loading...</p>;
  if (!data) return <p className="text-xs py-4" style={{ color: '#D4726A' }}>Unable to load.</p>;

  const byCategory = {};
  (data.checklist || []).forEach(c => {
    const cat = c.category || 'Other';
    if (!byCategory[cat]) byCategory[cat] = [];
    byCategory[cat].push(c);
  });

  return (
    <div className="space-y-3" data-testid="sop-training-panel">
      <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E5E1DC' }}>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>SOP Training Checklist</p>
            <p className="text-[11px]" style={{ color: '#9CA3AF' }}>{data.role} · {data.department} · {data.outlet}</p>
          </div>
          <div className="flex items-center gap-3">
            <div>
              <p className="text-[10px] uppercase" style={{ color: '#9CA3AF' }}>Completion</p>
              <p className="text-xl font-bold" style={{ color: data.completion_pct >= 80 ? '#2D8B5E' : data.completion_pct >= 50 ? '#D97706' : '#DC2626' }}>{data.completion_pct}%</p>
            </div>
            <div>
              <p className="text-[10px] uppercase" style={{ color: '#9CA3AF' }}>Done / Total</p>
              <p className="text-base font-bold" style={{ color: '#1B2A4A' }}>{data.completed}/{data.total}</p>
            </div>
          </div>
        </div>
        {data.total === 0 && (
          <p className="text-[11px] mt-3 px-3 py-2 rounded" style={{ backgroundColor: '#F7F5F2', color: '#6B7280' }}>
            No SOPs required yet. Assign SOPs to this person's role/department from the Outlet Intelligence → SOPs page.
          </p>
        )}
      </div>

      {Object.entries(byCategory).map(([cat, items]) => (
        <div key={cat} className="bg-white rounded-lg border" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-xs font-semibold px-4 pt-3 pb-2" style={{ color: '#1B2A4A' }}>{cat} ({items.length})</p>
          <div>
            {items.map((c, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-2 border-t" style={{ borderColor: '#F3F4F6' }}>
                <div className="w-5 h-5 rounded-full border flex items-center justify-center flex-shrink-0" style={{ borderColor: c.acknowledged ? '#2D8B5E' : '#E5E1DC', backgroundColor: c.acknowledged ? '#ECFDF5' : '#fff' }}>
                  {c.acknowledged && <Check size={12} style={{ color: '#2D8B5E' }} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate" style={{ color: '#1B2A4A' }}>{c.title} <span className="text-[10px] font-normal" style={{ color: '#9CA3AF' }}>v{c.version}</span></p>
                  <p className="text-[10px]" style={{ color: '#9CA3AF' }}>
                    {c.acknowledged ? `Acknowledged ${c.acknowledged_at ? new Date(c.acknowledged_at).toLocaleDateString() : ''}` : 'Pending your acknowledgement'}
                    {c.outlet_name && ` · ${c.outlet_name}`}
                  </p>
                </div>
                {c.document_url && <a href={c.document_url} target="_blank" rel="noreferrer" className="text-[10px] px-2 py-1 rounded bg-blue-50" style={{ color: '#185FA5' }}>Doc</a>}
                {!c.acknowledged && (
                  <button onClick={() => acknowledge(c)} className="text-[10px] px-3 py-1 rounded-md font-medium text-white" style={{ backgroundColor: '#2A7B88' }} data-testid={`ack-sop-${c.sop_id}`}>
                    I've read it
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
